import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { useTranslation, Trans } from 'react-i18next';
import { LoginForm } from '@hofsync/design-system';
import { authService } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import OTPInput from '../components/OTPInput';
import PasskeyRegister from '../components/PasskeyRegister';
import { landingFor } from '../utils/landing';
import { PHONE_COUNTRIES, defaultCountryForLanguage, parsePhoneToE164, type PhoneCountryCode } from '../utils/phone';
import './LoginPage.css';

const LoginPage: React.FC = () => {
    const [showOTP, setShowOTP] = useState(false);
    const [showPasskeySetup, setShowPasskeySetup] = useState(false);
    const [otpRequestTime, setOtpRequestTime] = useState<number>(0);
    const [normalizedPhone, setNormalizedPhone] = useState('');
    const [channel, setChannel] = useState<'sms' | 'email'>('sms');
    const [showPhoneForm, setShowPhoneForm] = useState(false);
    const [testOtp, setTestOtp] = useState<string | null>(null);
    const [initialIdentifier, setInitialIdentifier] = useState<string>('');
    const navigate = useNavigate();
    const { login, checkPasskeys, isLoading: authLoading, isAuthenticated, userProfile } = useAuth();
    const { showToast } = useToast();
    const { t, i18n } = useTranslation();
    // HOF-696 D19: national numbers are read against this country, not always +49.
    const [country, setCountry] = useState<PhoneCountryCode>(() => defaultCountryForLanguage(i18n.language));
    const rememberedSessionChecked = useRef(false);

    // Session-expired notice, once on mount.
    useEffect(() => {
        const sessionExpired = sessionStorage.getItem('session_expired');
        if (sessionExpired) {
            sessionStorage.removeItem('session_expired');
            showToast(t('login.sessionExpired'), 'warning');
        }
    }, [showToast, t]);

    // Remembered session (HOF-696 D5): decide only once AuthContext has finished
    // loading, so the landing reads the FETCHED profile — a worker seat goes to
    // /apps/labour, everyone else to /documents. Runs once: a later OTP login
    // flips `isAuthenticated` too, and that path navigates for itself.
    useEffect(() => {
        if (authLoading || rememberedSessionChecked.current) return;
        rememberedSessionChecked.current = true;

        if (isAuthenticated || authService.isAuthenticated()) {
            navigate(landingFor(userProfile), { replace: true });
            return;
        }

        // Prefill the identifier field with the last successful login on this
        // device (phone OR email) so users returning after auto-logout don't
        // have to retype it.
        const remembered = authService.getLastLoginPhone();
        if (remembered) {
            setInitialIdentifier(remembered);
        }

        setShowPhoneForm(true);
    }, [authLoading, isAuthenticated, userProfile, navigate]);

    const requestOTP = useMutation({
        mutationFn: (identifier: string) => authService.requestOTP(identifier),
        onSuccess: (data, variables) => {
            const isEmail = variables.includes('@');
            setShowOTP(true);
            setOtpRequestTime(Date.now());
            setNormalizedPhone(variables);
            setChannel(isEmail ? 'email' : 'sms');
            if (data.test_otp) setTestOtp(data.test_otp);
            showToast(t(isEmail ? 'login.otpSentEmail' : 'login.otpSent'), 'success');
        },
        onError: (error: any, variables) => {
            // Log the full error for debugging
            console.error('OTP Request Error:', error);
            console.error('Error response:', error.response);
            console.error('Error data:', error.response?.data);
            console.error('Error detail:', error.response?.data?.detail);

            // Check for INVITE_REQUIRED - redirect immediately
            // Handle both parsed JSON and string response data
            const responseData = error.response?.data;
            let detail: string | undefined;
            if (typeof responseData === 'string') {
                // Try to parse if it's a JSON string, otherwise use as-is
                try {
                    const parsed = JSON.parse(responseData);
                    detail = parsed?.detail;
                } catch {
                    detail = responseData;
                }
            } else {
                detail = responseData?.detail;
            }

            if (error.response?.status === 403 && detail === 'INVITE_REQUIRED') {
                navigate('/invite-required');
                return;
            }

            // Handle rate limiting
            if (error.response?.status === 429) {
                showToast(t('login.rateLimitError'), 'warning');
            } else {
                const isEmail = variables.includes('@');
                showToast(t(isEmail ? 'login.otpSendErrorEmail' : 'login.otpSendError'), 'error');
            }
        }
    });

    const handleOTPComplete = async (otp: string) => {
        try {
            // HOF-696 D5: land on the profile THIS login fetched — the context's
            // `userProfile` in this closure is still the pre-login one.
            const profile = await login(normalizedPhone, otp);
            showToast(t('login.loginSuccess'), 'success');

            // Platform admins to pruefstatus; everyone else by their seat.
            const cachedRoles = authService.getCachedUserRoles();
            const isAdmin = cachedRoles.includes('admin');
            navigate(isAdmin ? '/pruefstatus' : landingFor(profile));
        } catch (error: any) {
            console.error(error);

            // Check for INVITE_REQUIRED error from backend
            // Handle both parsed JSON and string response data
            const responseData = error.response?.data;
            let detail: string | undefined;
            if (typeof responseData === 'string') {
                try {
                    const parsed = JSON.parse(responseData);
                    detail = parsed?.detail;
                } catch {
                    detail = responseData;
                }
            } else {
                detail = responseData?.detail;
            }

            if (error.response?.status === 403 && detail === 'INVITE_REQUIRED') {
                navigate('/invite-required');
                return;
            }

            showToast(t('login.otpInvalid'), 'error');
        }
    };

    const displayIdentifier = useMemo(() => {
        if (!normalizedPhone) return '';
        if (channel === 'email') {
            // Mask the local part, keep the domain: anna@beispielhof.de ->
            // a***@beispielhof.de (recognisable without exposing the address).
            const at = normalizedPhone.indexOf('@');
            if (at <= 0) return normalizedPhone;
            return `${normalizedPhone.slice(0, 1)}***@${normalizedPhone.slice(at + 1)}`;
        }
        if (normalizedPhone.length < 6) return normalizedPhone;
        const suffix = normalizedPhone.slice(-2);
        return `${normalizedPhone.slice(0, 4)} ···· ${suffix}`;
    }, [normalizedPhone, channel]);

    // Show passkey registration prompt
    if (showPasskeySetup) {
        return (
            <PasskeyRegister
                onSkip={() => {
                    setShowPasskeySetup(false);
                    navigate(landingFor(userProfile));
                }}
                onSuccess={async () => {
                    await checkPasskeys();
                    setShowPasskeySetup(false);
                    navigate(landingFor(userProfile));
                }}
            />
        );
    }

    if (showOTP) {
        return (
            <div style={{ maxWidth: '480px', margin: '0 auto', padding: '40px 24px', textAlign: 'center' }}>
                {channel === 'email' ? (
                    <>
                        <p className="login-subline">
                            <Trans
                                i18nKey="login.codeSentToEmail"
                                values={{ email: displayIdentifier }}
                                components={{ strong: <strong /> }}
                            />
                        </p>
                        <p className="login-subline login-spam-hint">{t('login.emailSpamHint')}</p>
                    </>
                ) : (
                    <p className="login-subline">
                        <Trans
                            i18nKey="login.codeSentTo"
                            values={{ phone: displayIdentifier }}
                            components={{ strong: <strong /> }}
                        />
                    </p>
                )}

                <div className="otp-wrapper">
                    <OTPInput
                        length={6}
                        onComplete={handleOTPComplete}
                        onResend={() => {
                            requestOTP.mutate(normalizedPhone);
                        }}
                        expiresAt={otpRequestTime + 5 * 60 * 1000}
                    />
                </div>

                {testOtp && (
                    <div style={{
                        margin: '16px 0',
                        padding: '10px 16px',
                        background: '#fffbe6',
                        border: '1px solid #ffe58f',
                        borderRadius: '6px',
                        fontSize: '13px',
                        color: '#874d00',
                    }}>
                        <strong>DEV:</strong> OTP = <code style={{ fontSize: '15px', letterSpacing: '2px' }}>{testOtp}</code>
                    </div>
                )}

                <button
                    className="login-inline-button"
                    onClick={() => {
                        setShowOTP(false);
                        setTestOtp(null);
                        showToast(t(channel === 'email' ? 'login.changeEmailInfo' : 'login.changeNumberInfo'), 'info');
                    }}
                    type="button"
                >
                    {t(channel === 'email' ? 'login.changeEmail' : 'login.changeNumber')}
                </button>
            </div>
        );
    }

    // Don't show form until ready
    if (!showPhoneForm) {
        return (
            <div style={{ maxWidth: '480px', margin: '0 auto', padding: '40px 24px', textAlign: 'center' }}>
                <p className="login-subline">{t('login.preparingAuth')}</p>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '480px', margin: '0 auto', padding: '40px 24px' }}>
            <label className="login-country">
                <span className="login-country__label">{t('login.country')}</span>
                <select
                    className="login-country__select"
                    value={country}
                    onChange={(e) => setCountry(e.target.value as PhoneCountryCode)}
                >
                    {PHONE_COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>
                            {c.code} {c.dial}
                        </option>
                    ))}
                </select>
                <span className="login-country__hint">{t('login.countryHint')}</span>
            </label>
            <LoginForm
                identifierPlaceholder={t('login.identifierPlaceholder')}
                initialIdentifier={initialIdentifier}
                showTerms={true}
                termsIntro={t('login.termsIntro')}
                termsText={t('login.termsText')}
                privacyText={t('login.privacyText')}
                termsUrl="/terms"
                privacyUrl="/privacy"
                isLoading={requestOTP.isPending}
                onTermsError={() => {
                    showToast(t('login.termsRequired'), 'warning');
                }}
                onOtpRequest={(identifier) => {
                    // #2008c: auto-detect the channel. An '@' routes to the
                    // email path — trim + lowercase, NO phone munging — while
                    // everything else keeps the existing phone normalization.
                    if (identifier.includes('@')) {
                        requestOTP.mutate(identifier.trim().toLowerCase());
                        return;
                    }

                    // HOF-696 D19: `+…` / `00…` are taken as international; a
                    // national number gets the selected country's code (it used
                    // to default to +49, which sent a Romanian worker's SMS to a
                    // German number that does not exist).
                    const parsed = parsePhoneToE164(identifier, country);
                    if (!parsed.ok) {
                        showToast(t('errors.INVALID_PHONE_NUMBER'), 'warning');
                        return;
                    }
                    requestOTP.mutate(parsed.e164);
                }}
            />
        </div>
    );
};

export default LoginPage;
